<template>
  <v-row
    :key="itemId"
    no-gutters
    class="session-divider"
  >
    <div
      class="session-divider__title-area"
    >
      <header
        rows="1"
        class="session-divider__title"
      >
        {{ translatedTitle }}
      </header>
      <div
        v-if="isEditor"
        class="session-divider__edit-title"
      >
        <CardOptions
          @rename="openModal()"
          @duplicate="duplicateCard()"
          @delete="deleteCard()"
        />
      </div>
    </div>
    <section
      v-show="annotation"
      class="session-divider__description-area"
    >
      <pre
        class="session-divider__description"
      >
      {{ annotation }}
      </pre>
    </section>
    <div class="session-divider__title-edit-modal">
      <AddDescriptionModal
        v-if="editModal"
        :title="translatedTitle"
        :description="annotation"
        :title-max-characters="titleMaxCharacters"
        :description-max-characters="annotationMaxCharacters"
        @cancel="cancelAction"
        @confirm="confirmAction"
      />
    </div>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
import cardsModel from '@/mixins/cards-model';
import AddDescriptionModal from '@/components/AddDescriptionModal';
import CardOptions from '@/components/cards/models/CardOptions';
import { generateRandomId } from '@/utils/math';
import { trackEventWithUserProperties } from '@/utils/mixpanel';

export default {
  name: 'SessionDividerWithTitle',
  components: {
    AddDescriptionModal,
    CardOptions,
  },
  mixins: [
    cardsModel,
  ],
  props: {
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      editModal: false,
      optionsSelected: false,
      title: '',
      originalTitle: '',
      annotation: '',
      originalAnnotation: '',
      inputLoading: false,
      titleMaxCharacters: 40,
      annotationMaxCharacters: 340,
    };
  },
  computed: {
    ...mapGetters('user', [
      'isEditor',
    ]),
    translatedTitle() {
      if (this.isDefaultSessionDividerTitle(this.title)) {
        return this.$t('library.LSN_25.title');
      }

      return this.title;
    },
  },
  mounted() {
    if (this.card) {
      this.originalTitle = this.card.props.text || '';
      this.title = this.originalTitle;

      this.originalAnnotation = this.card.props.annotation || '';
      this.annotation = this.originalAnnotation;
      return true;
    }

    console.error('Could not find the card', this.itemId);
  },
  methods: {
    isDefaultSessionDividerTitle(title) {
      const defaultTitles = [
        'Divisor de seção',
        'Section Divider',
        'Divisor de Sección',
      ];

      return defaultTitles.includes((title || '').trim());
    },
    openModal() {
      this.editModal = true;

      trackEventWithUserProperties('ST Card Options Rename', {
        Widget: this.card.component,
        Title: this.title,
        Description: this.annotation,
      });
    },
    confirmAction({ title, description }) {
      this.inputLoading = true;

      this.card.props.text = title;
      this.card.props.annotation = description;
      this.annotation = description;

      trackEventWithUserProperties('ST SectionDivider TextBox Edit', {
        Title: this.card.props.text,
        Description: this.card.props.annotation,
      });

      this.card.id = generateRandomId();

      this.saveCards().then(() => {
        this.originalTitle = this.title;
        this.originalAnnotation = description;
        this.inputLoading = false;
      });

      this.editModal = false;
    },
    cancelAction() {
      this.title = this.originalTitle;
      this.annotation = this.originalAnnotation;

      this.editModal = false;

      trackEventWithUserProperties('ST SectionDivider TextBox Cancel', { Widget: this.card.component });
    },
  },
};
</script>

<style scoped lang="scss">
  .session-divider {
    flex-direction: column;

    &__title-area {
      display: flex;
      align-items: center;
    }

    &__title {
      display: initial;
      font-style: normal;
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-lg);
      line-height: 32px;
      margin: var(--ds-spacing-stack-sm) 0 var(--ds-spacing-stack-nano) 0;
      color: var(--ds-color-text-1);
      white-space: nowrap;
    }

    &__edit-title {
      margin-top: var(--ds-spacing-stack-xs);
    }

    &__description-area {
      max-width: calc(100vw - 84px);
    }

    &__description {
      margin: 0;
      font: var(--ds-font-family);
      color: var(--ds-color-text-2);
      font-weight: var(--ds-font-weight-regular);
      font-size: var(--ds-font-size-baseline);
      line-height: var(--ds-font-line-height-normal);
      text-align: left;
      white-space: pre-line;
    }

    &__session-options {
      display: flex;
      width: 153px;
      height: 136px;
    }

    &__title-edit-modal {
      z-index: 10;
    }
  }

  ::v-deep {
    .v-list {
      padding: var(--ds-spacing-inset-quark);
    }

    .v-list-item {
      width: 145px;
      height: 20px;
    }
  }
</style>
